/**
 * VotingButton Component
 *
 * Eye-catching button for employee of the month voting
 * - Pulsing gold animation when voting is active
 * - Badge showing days remaining
 * - Different states: inactive, active, voted
 * - Opens VotingModal when clicked
 */

import { useState } from 'react';
import { Trophy, CheckCircle } from 'lucide-react';
import { useVoting } from '@/hooks/useVoting';
import VotingModal from './VotingModal';

interface VotingButtonProps {
  userId: string | null;
  userName: string;
  tenantId?: string;
}

export default function VotingButton({ userId, userName, tenantId }: VotingButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { votingPeriod, isVotingOpen, hasVoted, daysRemaining, hoursRemaining, loading } =
    useVoting(userId, userName, tenantId);

  // Don't render if not logged in or loading
  if (!userId || loading) {
    return null;
  }

  // Don't render if no voting period
  if (!isVotingOpen || !votingPeriod) {
    return null;
  }

  const handleClick = () => {
    setIsModalOpen(true);
  };

  // Calculate time remaining text
  const getTimeRemainingText = () => {
    if (daysRemaining > 0) {
      return `${daysRemaining}d`;
    } else if (hoursRemaining > 0) {
      return `${hoursRemaining}h`;
    } else {
      return 'Ending';
    }
  };

  return (
    <>
      {/* Button */}
      <button
        onClick={handleClick}
        className={`
          relative
          flex items-center gap-2
          px-3 py-2
          rounded-lg
          font-medium
          transition-all duration-300
          ${
            hasVoted
              ? 'bg-brand-success/20 text-brand-success hover:bg-brand-success/30'
              : 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:from-amber-600 hover:to-yellow-600'
          }
          ${!hasVoted ? 'animate-pulse-glow' : ''}
          shadow-lg hover:shadow-xl
          transform hover:scale-105
        `}
        title={
          hasVoted
            ? "You've voted!"
            : `Vote for Employee of the Month - ${getTimeRemainingText()} remaining`
        }
      >
        {/* Icon */}
        {hasVoted ? (
          <CheckCircle className="w-5 h-5" />
        ) : (
          <Trophy className="w-5 h-5 animate-bounce-subtle" />
        )}

        {/* Text */}
        <span className="hidden sm:inline text-sm">
          {hasVoted ? 'Voted' : 'Vote Now'}
        </span>

        {/* Badge - Days/Hours Remaining (only show if not voted) */}
        {!hasVoted && (
          <span
            className="
              absolute -top-2 -right-2
              bg-red-500 text-white
              text-xs font-bold
              px-2 py-0.5
              rounded-full
              shadow-lg
              animate-pulse
            "
          >
            {getTimeRemainingText()}
          </span>
        )}

        {/* Glow effect for active voting */}
        {!hasVoted && (
          <span
            className="
              absolute inset-0
              rounded-lg
              bg-gradient-to-r from-amber-400 to-yellow-400
              opacity-20
              blur-md
              animate-pulse-glow-bg
            "
          />
        )}
      </button>

      {/* Modal */}
      <VotingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userId={userId}
        userName={userName}
        votingPeriod={votingPeriod}
        hasVoted={hasVoted}
        tenantId={tenantId}
      />

      {/* Custom animations in inline style */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(251, 191, 36, 0.5), 0 0 40px rgba(251, 191, 36, 0.3);
          }
          50% {
            box-shadow: 0 0 30px rgba(251, 191, 36, 0.8), 0 0 60px rgba(251, 191, 36, 0.5);
          }
        }

        @keyframes pulse-glow-bg {
          0%, 100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.4;
          }
        }

        @keyframes bounce-subtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        .animate-pulse-glow-bg {
          animation: pulse-glow-bg 2s ease-in-out infinite;
        }

        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}
